import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

const STORAGE_KEY = 'superCalculator.globalSettings';
// Bank / financial-institution loans are stress-tested at the MAS medium-term interest
// rate floor (4% p.a. for residential property since 30 Sep 2022) for TDSR and MSR.
const DEFAULT_STRESS_TEST_RATE = 4;
// HDB concessionary loans are not FI loans: HDB computes the eligible loan amount at the
// higher of its own interest rate floor (3.0% p.a. since 30 Sep 2022) and the prevailing
// HDB rate (2.6%), so 3% is the floor that binds.
const DEFAULT_HDB_STRESS_TEST_RATE = 3;
// Text-size preference: 0 = app default, each step ≈ +1px on the root rem base.
const DEFAULT_FONT_SCALE_PT = 1;
const FONT_SCALE_MIN = -2;
const FONT_SCALE_MAX = 6;

const clampFontScale = (v: number): number =>
  Math.min(FONT_SCALE_MAX, Math.max(FONT_SCALE_MIN, Math.round(v)));

interface GlobalCalculatorSettings {
  /** Stress test rate for bank loans (MAS medium-term interest rate floor). */
  stressTestRate: number;
  /** Stress test rate for HDB concessionary loans (HDB's interest rate floor). */
  hdbStressTestRate: number;
  fontScalePt: number;
}

interface GlobalCalculatorSettingsContextType {
  settings: GlobalCalculatorSettings;
  updateSettings: (next: Partial<GlobalCalculatorSettings>) => void;
}

const GlobalCalculatorSettingsContext = createContext<GlobalCalculatorSettingsContextType | undefined>(undefined);

const DEFAULTS: GlobalCalculatorSettings = {
  stressTestRate: DEFAULT_STRESS_TEST_RATE,
  hdbStressTestRate: DEFAULT_HDB_STRESS_TEST_RATE,
  fontScalePt: DEFAULT_FONT_SCALE_PT,
};

const readInitialSettings = (): GlobalCalculatorSettings => {
  if (typeof window === 'undefined') return DEFAULTS;

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;

    const parsed = JSON.parse(raw) as Partial<GlobalCalculatorSettings>;
    const parsedRate = Number(parsed.stressTestRate);
    const parsedHdbRate = Number(parsed.hdbStressTestRate);
    const parsedFont = Number(parsed.fontScalePt);

    return {
      stressTestRate: Number.isFinite(parsedRate) && parsedRate > 0 ? parsedRate : DEFAULT_STRESS_TEST_RATE,
      // Settings saved before the HDB rate existed fall back to its default, not to the
      // bank rate — an old 4% must not silently carry over onto HDB loans.
      hdbStressTestRate: Number.isFinite(parsedHdbRate) && parsedHdbRate > 0 ? parsedHdbRate : DEFAULT_HDB_STRESS_TEST_RATE,
      fontScalePt: Number.isFinite(parsedFont) ? clampFontScale(parsedFont) : DEFAULT_FONT_SCALE_PT,
    };
  } catch {
    return DEFAULTS;
  }
};

export const GlobalCalculatorSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<GlobalCalculatorSettings>(readInitialSettings);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  // Apply the text-size preference by nudging the root rem base: rem-based fonts scale while
  // px-based panel geometry stays fixed. A single app-level effect avoids the conflict of
  // multiple mounted calculators each writing document root font-size. The base is captured
  // once, and the override is cleared on unmount so the app default is restored.
  const baseRootFontRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    if (baseRootFontRef.current === null) {
      baseRootFontRef.current = parseFloat(getComputedStyle(html).fontSize) || 16;
    }
    html.style.fontSize = `${baseRootFontRef.current + settings.fontScalePt}px`;
    return () => { html.style.fontSize = ''; };
  }, [settings.fontScalePt]);

  const updateSettings = (next: Partial<GlobalCalculatorSettings>) => {
    setSettings((prev) => ({
      ...prev,
      ...next,
      stressTestRate:
        next.stressTestRate != null && Number.isFinite(next.stressTestRate) && next.stressTestRate > 0
          ? next.stressTestRate
          : prev.stressTestRate,
      hdbStressTestRate:
        next.hdbStressTestRate != null && Number.isFinite(next.hdbStressTestRate) && next.hdbStressTestRate > 0
          ? next.hdbStressTestRate
          : prev.hdbStressTestRate,
      fontScalePt:
        next.fontScalePt != null && Number.isFinite(next.fontScalePt)
          ? clampFontScale(next.fontScalePt)
          : prev.fontScalePt,
    }));
  };

  return (
    <GlobalCalculatorSettingsContext.Provider value={{ settings, updateSettings }}>
      {children}
    </GlobalCalculatorSettingsContext.Provider>
  );
};

export const useGlobalCalculatorSettings = () => {
  const context = useContext(GlobalCalculatorSettingsContext);
  if (!context) {
    throw new Error('useGlobalCalculatorSettings must be used within GlobalCalculatorSettingsProvider');
  }
  return context;
};

export { DEFAULT_STRESS_TEST_RATE, DEFAULT_HDB_STRESS_TEST_RATE, DEFAULT_FONT_SCALE_PT, FONT_SCALE_MIN, FONT_SCALE_MAX };
